<!--@format-->
<template>
    <div class="syncro-send-contact-widget">
        <div class="widget-header">
            <h3>Send To Syncro</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div v-if="isInitializing" class="loading-container">
            <loading-screen-svg />
        </div>
        <div v-else class="content-wrapper">
            <syncro-send-contact
                v-if="contact"
                :contact="contact"
                :company="company"
                @select-company="selectCompany"
                @remove-company="removeCompany"
            />
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" :on-click="closeWidget"> Cancel </app-button>
            </div>
            <div class="action-container">
                <app-button
                    :on-click="sendContactToSyncro"
                    :loading="isSendingContactToSyncro"
                    :disabled="isSendingContactToSyncro || deactivatedSwitches"
                >
                    Send to Syncro
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import log, { LogLevel } from "@/scripts/logger";

const AppButton = () => import('@/components/common/app-button');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const SyncroSendContact = () => import('@/components/integration/syncro-send-contact');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'syncro-send-contact-widget',

    components: {
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'syncro-send-contact': SyncroSendContact,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            contact: {
                id: null,
                name: null,
                profileImageUrl: null,
                shouldSendInformation: true,
                shouldSendOpportunities: false,
                errorMessage: null,
                isUpsert: null,
                clientId: null,
                syncroContactId: null,
            },

            company: {
                id: null,
                name: null,
                imageUrl: null,
                syncroCompanyId: null,
            },

            isSavingCompanyToContact: false,
            isSendingContactToSyncro: false,
            deactivatedSwitches: false,
            syncroContactLinkData: {
                site: null,
                syncroContactId: null,
            },

            isInitializing: true,
        };
    },

    computed: {
        syncroLink: function () {
            return `https://${this.syncroContactLinkData.site}.syncromsp.com/customers/${this.company.syncroCompanyId}#contacts`;
        },
    },

    watch: {
        'contact.shouldSendInformation': function () {
            this.deactivatedSwitches = !(
                this.contact.shouldSendInformation || this.contact.shouldSendOpportunities
            );
        },

        'contact.shouldSendOpportunities': function () {
            this.deactivatedSwitches = !(
                this.contact.shouldSendInformation || this.contact.shouldSendOpportunities
            );
        },
    },

    created() {
        this.getData();
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getData() {
            this.getContact()
                .then(this.getIntegrationInformation)
                .catch((error) => {
                    this.$helpers.showFailNotification(error, { error });
                });
        },

        getContact() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(
                        `/service/Contacts?$filter=Id eq ${this.widget.options.id}&$expand=Company`
                    )
                    .then((response) => {
                        this.createContact(response.data.value[0]);

                        if (response.data.value[0].Company) {
                            this.createCompany(response.data.value[0].Company);
                        }

                        resolve();
                    })
                    .catch(() => {
                        reject('An error occurred while fetching the contacts you selected.');
                    });
            });
        },

        getIntegrationInformation() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/ms/syncro-api/v1/credentials/${this.contact.clientId}`)
                    .then((response) => {
                        this.syncroContactLinkData.site = response.data.SubDomain;
                    })
                    .catch(() => {
                        reject('An error occurred while fetching your Syncro integration info.');
                    })
                    .finally(() => {
                        this.isInitializing = false;
                    });
            });
        },

        createContact(contactsResponse) {
            this.contact.id = contactsResponse.Id;
            this.contact.name = this.$helpers.getName(
                contactsResponse.FirstName,
                contactsResponse.LastName
            );
            this.contact.profileImageUrl =
                contactsResponse.ProfileImageUrl || require('@/assets/images/avatar.png');
            this.contact.shouldSendInformation = true;
            this.contact.shouldSendOpportunities = false;
            this.contact.errorMessage = null;
            this.contact.selectedOption = contactsResponse.IsUpsert ? 'upsert' : 'insert';
            this.contact.clientId = contactsResponse.ClientId;
        },

        createCompany(company) {
            this.company.id = company.Id;
            this.company.name = company.Name;
            this.company.imageUrl =
                company.LogoUrl || require('@/assets/images/company-avatar.png');
            this.company.syncroCompanyId = company.SyncroCompanyId;
        },

        selectCompany(company) {
            this.company = {
                id: company.id,
                name: company.name,
                imageUrl: company.imageUrl,
            };

            this.saveCompanyToContact();
        },

        removeCompany() {
            this.company = null;
        },

        saveCompanyToContact() {
            this.isSavingCompanyToContact = true;

            var changes = [
                {
                    name: 'CompanyId',
                    value: this.company.id,
                },
            ];

            this.$api
                .patch(`/api/Contacts/${this.widget.options.id}`, changes)
                .then(() => {
                    this.isSavingCompanyToContact = false;
                    this.$helpers.showSuccessNotification(
                        `<strong>${this.contact.name}</strong> has been added to ${this.company.name}`
                    );
                })
                .catch((error) => {
                    this.isSavingCompanyToContact = false;
                    this.$helpers.showFailNotification(`Unable to add ${this.contact.name} to ${this.company.name}.`,
                        { level: LogLevel.DEBUG }
                    );
                    log.error('Unable to add contact to company', error);
                });
        },

        sendContactToSyncro() {
            this.isSendingContactToSyncro = true;

            this.sendCompanyInfo()
                .then(this.sendContactInfo)
                .then(this.getSyncroContactId)
                .then(() => {
                    this.isSendingContactToSyncro = false;
                    this.contact.errorMessage = null;

                    this.createSuccessMessage();

                    EventBus.$emit('contact:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.isSendingContactToSyncro = false;
                    this.contact.errorMessage = error.Message;
                });
        },

        getSyncroContactId() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(
                        `/service/Contacts?$filter=Id eq ${this.contact.id} &$select=SyncroContactId`
                    )
                    .then((response) => {
                        this.syncroContactLinkData.syncroContactId =
                            response.data.value[0].SyncroContactId;
                        resolve();
                    })
                    .catch((response) => {
                        reject(response.response.data);
                    });
            });
        },

        createSuccessMessage() {
            var actionTaken = this.contact.isUpsert ? 'updated' : 'inserted';
            var message = `<strong>${this.contact.name}</strong> has been ${actionTaken} in Syncro.`;

            EventBus.$emit('show-notification', {
                iconUrl: require('@/assets/images/SVG/checkmark.svg'),
                isIconSvg: true,
                message: message,
                actionLabel: 'View',
                link: this.syncroLink,
                action: null,
            });
        },

        sendCompanyInfo() {
            return new Promise((resolve, reject) => {
                if (this.company && !this.company.syncroCompanyId) {
                    const body = {
                        isUpsert: false,
                    };

                    this.$api
                        .post(
                            `/ms/syncro-api/v1/integrations/export/companies/${this.company.id}/${this.contact.clientId}`,
                            body
                        )
                        .then(() => {
                            this.$helpers.showSuccessNotification(
                                `${this.company.name} has been inserted in Syncro`
                            );
                            resolve();
                        })
                        .catch((response) => {
                            reject(response.response.data);
                        });
                } else {
                    resolve();
                }
            });
        },

        sendContactInfo() {
            return new Promise((resolve, reject) => {
                const body = {
                    isUpsert: this.contact.isUpsert,
                    exportContacts: this.contact.shouldSendInformation,
                    exportDeals: this.contact.shouldSendOpportunities,
                };

                this.$api
                    .post(
                        `/ms/syncro-api/v1/integrations/export/contacts/${this.contact.id}/${this.contact.clientId}`,
                        body
                    )
                    .then(() => {
                        resolve();
                    })
                    .catch((response) => {
                        reject(response.response.data);
                    });
            });
        },
    },
};
</script>

<style scoped>
.syncro-send-contact-widget .content-wrapper {
    padding-left: 0px;
    padding-right: 0px;
}

.syncro-send-contact-widget .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.syncro-send-contact-widget .action-container .btn.transparent-btn {
    background: transparent;
    color: #18aeff;
}

.syncro-send-contact-widget .action-container .btn.transparent-btn:hover {
    background: #dbedfa;
}

.syncro-send-contact-widget .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.syncro-send-contact-widget .company-options-container h4 {
    margin-bottom: 20px;
}

.syncro-send-contact-widget .widget-content .processing {
    display: block;
    width: 200px;
    margin: 0 auto;
}

.syncro-send-contact-widget .widget-content .row {
    margin-bottom: 30px;
}

.syncro-send-contact-widget .loading-container {
    margin: auto;
}
</style>
